import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { Container, Card, Grid, Header } from 'semantic-ui-react';
import { ApolloError } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import { GraphQLError } from 'graphql';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { isEmpty, pathOr, reject } from 'ramda';

import { useCategories } from 'helpers/useCategories';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import FormInput from 'components/FormComponents/FormInput';
import FormSelectList from 'components/FormComponents/FormSelect';
import ControlledFormInput from 'components/FormComponents/ControlledFormInput';
import FormTextArea from 'components/FormComponents/FormTextArea';
import DynamicFormInput from 'components/FormComponents/DynamicFormInput';
import ColorPicker from 'components/FormComponents/ColorPicker';
import UploadWidget from 'components/FormComponents/UploadWidget';
import {
  AssetBundle,
  AssetName,
} from 'components/FormComponents/UploadWidget/UploadWidget.helpers';

import styles from './CreateMerchant.styles';
import {
  CREATE_MERCHANT,
  CREATE_MERCHANT_BRAND_ASSETS,
} from './CreateMerchant.queries';
import {
  getImageDetails,
  buildBrandAssetImage,
  buildBrandAssetColor,
  buildBundleComponent,
  buildMatchers,
  BrandTypename,
  BrandAssetComponent,
} from './CreateMerchant.helpers';

type Styles = ClassProp<ReturnType<typeof styles>>;

type CreateMerchantResponse = {
  createMerchant: {
    success: boolean;
  };
};

type AddBrandAssetsResponse = {
  upsertBrandAsset: {
    success: boolean;
  };
  setBrandTransactionMatchers: {
    success: boolean;
  };
  setBrandPlaceMatchers: {
    success: boolean;
  };
};

type Props = {
  classes: Styles;
};

const getApolloError = (errors?: ApolloError | GraphQLError[]) => {
  console.error('errors: ', errors);
  return pathOr('', ['graphQLErrors'], errors);
};

const RequiredFieldMsg = 'This field is required';
const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Merchant creation successful.',
  },
];

type MerchantDetails = {
  merchantUuid: string;
  merchantName: string;
  description: string;
  logo: string;
  banner: string;
  card: string;
  featuredBanner: string;
  featuredBannerHeadline: string;
  primaryColor: string;
  secondaryColor: string;
  brandMatchers: [
    {
      name: string;
    },
  ];
  placesMatchers: [
    {
      name: string;
    },
  ];
  category: string;
  subCategory: string;
  mccCodeMerchant: boolean;
};

const CreateMerchant: FunctionComponent<Props> = ({ classes }) => {
  const [merchantName, setMerchantName] = useState<string>('');
  const [merchantId, setMerchantId] = useState<string>('');
  const [brandAssetVariables, setBrandAssetVariables] = useState({});
  const history = useHistory();
  const methods = useForm<MerchantDetails>({
    mode: 'onBlur',
    defaultValues: {
      brandMatchers: [{ name: '' }],
      placesMatchers: [{ name: '' }],
    },
  });

  const { register, handleSubmit } = methods;

  const handleError = (error: any) =>
    toastMapping({}, 'error')(getApolloError(error) as any);

  const handleSuccess = (success: any) =>
    toastMapping({}, 'success')(success as any);

  const handleMerchantCreationResponse = (
    createMerchantResponse: CreateMerchantResponse,
  ) => {
    const success = pathOr(
      false,
      ['createMerchant', 'success'],
      createMerchantResponse,
    );

    if (success) {
      addBrandAssets({ ...brandAssetVariables });
    }
  };

  const handleBrandAssetsResponse = (
    addBrandAssetsResponse: AddBrandAssetsResponse,
  ) => {
    const success = pathOr(
      false,
      ['upsertBrandAsset', 'success'],
      addBrandAssetsResponse,
    );

    if (success) {
      return handleSuccess(defaultSuccessMessage);
    }
  };

  const [createMerchant, { loading: isSubmitting }] = useMutation(
    CREATE_MERCHANT,
    {
      onCompleted: handleMerchantCreationResponse,
      onError: handleError,
    },
  );

  const [addBrandAssets, { loading: addingBrands }] = useMutation(
    CREATE_MERCHANT_BRAND_ASSETS,
    {
      onCompleted: handleBrandAssetsResponse,
      onError: handleError,
    },
  );

  const { categoryOptions, subCategoryOptions, setCategory } = useCategories();

  useEffect(() => {
    register({ name: 'category' });
    register({ name: 'subCategory' });
    setMerchantId(uuid());
  }, []);

  const onSubmit = ({
    merchantUuid,
    merchantName,
    description,
    logo,
    banner,
    card,
    featuredBanner,
    featuredBannerHeadline,
    primaryColor,
    secondaryColor,
    brandMatchers,
    placesMatchers,
    category,
    subCategory,
    mccCodeMerchant,
  }: MerchantDetails) => {
    const { path, publicId } = getImageDetails(logo);
    const brandId = mccCodeMerchant
      ? ('VENMO_' + merchantUuid.replace(/-/g, '')).slice(0, 32)
      : merchantUuid.replace(/-/g, '');
    const categories = [];
    if (category) {
      categories.push(category);
    }
    if (subCategory) {
      categories.push(subCategory);
    }

    const icon = buildBrandAssetImage(
      logo,
      'image',
      BrandAssetComponent.icon,
      BrandTypename.BrandAssetIcon,
    );
    const cardImg = buildBrandAssetImage(
      card,
      'image',
      BrandAssetComponent.cardImage,
      BrandTypename.BrandAssetCard,
    );
    const bannerImg = buildBrandAssetImage(
      banner,
      'image',
      BrandAssetComponent.banner,
      BrandTypename.BrandAssetBanner,
    );
    const featuredBannerImg = buildBrandAssetImage(
      featuredBanner,
      'image',
      BrandAssetComponent.featuredBanner,
      BrandTypename.BrandAssetFeaturedBanner,
    );
    const featuredBannerHeadlineCurr = buildBundleComponent(
      featuredBannerHeadline,
      'template',
      BrandAssetComponent.featuredBannerHeadline,
      BrandTypename.BrandAssetFeaturedBannerHeadline,
    );
    const primary = buildBrandAssetColor(
      primaryColor,
      'color',
      BrandAssetComponent.primaryColor,
      BrandTypename.BrandAssetPrimaryColor,
    );
    const secondary = buildBrandAssetColor(
      secondaryColor,
      'color',
      BrandAssetComponent.secondaryColor,
      BrandTypename.BrandAssetSecondaryColor,
    );

    const places = buildMatchers(placesMatchers.map((matcher) => matcher.name));
    const transactions = buildMatchers(
      brandMatchers.map((matcher) => matcher.name),
    );

    const assets = reject(isEmpty, [
      icon,
      cardImg,
      bannerImg,
      featuredBannerImg,
      featuredBannerHeadlineCurr,
      primary,
      secondary,
    ]);

    createMerchant({
      variables: {
        brandId,
        categories,
        action: 'ADD',
        params: {
          merchantUuid,
          merchantName,
          brandId,
          publicId: encodeURI(publicId),
          imagePath: path,
          description: description.split('\n').join(' '),
          merchantSourceCode: undefined,
        },
      },
    });

    const variables = {
      variables: {
        brandId,
        assets,
        bundle: 'DEFAULT_BRANDING',
        brandMatchers: transactions,
        placesMatchers: places,
      },
    };
    setBrandAssetVariables(reject(isEmpty, variables));
  };

  return (
    <Container className={classes.container}>
      <Grid className={classes.centerColumn}>
        <Grid.Column className={classes.columnWide}>
          <Header className={classes.header} as="h1">
            Create Merchant
          </Header>
          <Card className={classes.cardContainer} fluid centered>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <input
                  type="hidden"
                  name="merchantUuid"
                  value={merchantId}
                  ref={register}
                />
                <Grid className={classes.formGrid} columns={2}>
                  <Grid.Row>
                    <Grid.Column>
                      <ControlledFormInput
                        name="merchantName"
                        type="text"
                        placeholder="Merchant Name"
                        label="Merchant Name"
                        register={register({ required: RequiredFieldMsg })}
                        onChange={setMerchantName}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <FormInput
                        name="mccCodeMerchant"
                        type="checkbox"
                        label="MCC Code Merchant"
                        register={register}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <FormTextArea
                        name="description"
                        type="text"
                        placeholder="Enter a brief description about the merchant"
                        label="Merchant Description"
                        register={register()}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <UploadWidget
                        name="logo"
                        label="Logo"
                        merchantName={merchantName}
                        assetBundle={AssetBundle.DEFAULT_BRANDING}
                        assetName={AssetName.LOGO}
                        size="small"
                        register={register({ required: RequiredFieldMsg })}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <UploadWidget
                        name="card"
                        label="Merchandising Card"
                        merchantName={merchantName}
                        assetBundle={AssetBundle.DEFAULT_BRANDING}
                        assetName={AssetName.CARD}
                        register={register()}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <UploadWidget
                        name="banner"
                        label="Detail Header"
                        merchantName={merchantName}
                        assetBundle={AssetBundle.DEFAULT_BRANDING}
                        assetName={AssetName.BANNER}
                        register={register()}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <UploadWidget
                        name="featuredBanner"
                        label="Featured Banner"
                        merchantName={merchantName}
                        assetBundle={AssetBundle.DEFAULT_BRANDING}
                        assetName={AssetName.FEATUREDBANNER}
                        assetFolder="featured/dosh"
                        filePrefix="Featured_Banner_Header_"
                        register={register()}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <FormTextArea
                        name="featuredBannerHeadline"
                        type="text"
                        placeholder="Enter featured banner headline"
                        label="Featured Banner Headline"
                        register={register()}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <FormSelectList
                        label="Category"
                        name="category"
                        dropDownOptions={categoryOptions}
                        onSelectChange={setCategory}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <FormSelectList
                        label="Sub Category"
                        name="subCategory"
                        dropDownOptions={subCategoryOptions}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <DynamicFormInput
                        name="brandMatchers"
                        type="text"
                        placeholder="Transaction Matchers"
                        label="Transaction Matchers"
                        appendLabel="Add Matcher"
                        register={register()}
                        size="large"
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <DynamicFormInput
                        name="placesMatchers"
                        type="text"
                        placeholder="Places Matchers"
                        label="Places Matchers"
                        appendLabel="Add Matcher"
                        register={register()}
                        size="large"
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row columns={4}>
                    <Grid.Column>
                      <ColorPicker
                        type="text"
                        placeholder="Primary Color"
                        label="Primary Color"
                        register={register()}
                        name="primaryColor"
                        defaultValue={theme.violet}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <ColorPicker
                        type="text"
                        placeholder="Secondary Color"
                        label="Secondary Color"
                        register={register()}
                        name="secondaryColor"
                        defaultValue={theme.softSeafoamGreen}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <Grid>
                  <Grid.Row className={classes.formRow}>
                    <button
                      className={classnames(
                        classes.button,
                        classes.buttonSecondary,
                        classes.mediumBtn,
                      )}
                      onClick={history.goBack}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={classnames(classes.button, classes.mediumBtn)}
                    >
                      {isSubmitting || addingBrands ? '...Submitting' : 'Save'}
                    </button>
                  </Grid.Row>
                </Grid>
              </form>
            </FormProvider>
          </Card>
        </Grid.Column>
      </Grid>
    </Container>
  );
};

export default withStyles(styles)(CreateMerchant);
